import { useCallback, useEffect, useState } from "react";
import getCMResponse from "../../../utils/api_manager";
// import checkmarkIcon from '../../../assets/icons/checkmark.svg';
import styles from './GoogleAnalyticsAuth.module.css';
import GoogleAnalyticsIcon from '../../../assets/icons/google-analytics.svg';
import GoogleDataAccess from "../GoogleDataAccess/GoogleDataAccess";
import AccountSelector from "./AccountSelector/AccountSelector";
import PropertySelector from "./PropertySelector/PropertySelector";

const GoogleAnalyticsAuth = ({
    grantedScopes = [],
    requiredScopes = [],
    onGrantedScopesUpdate,
    project,
    providerProjectId,
    onLoginReady,
    isConnected,
    // Grant-access loader centralization (requirement 3): raised for the
    // full account-list-fetch -> property-list-fetch span after the OAuth
    // popup closes (nothing else renders during that gap), and again for
    // the final property-save call. Optional -- GrantOnboardingAccess is the
    // only caller that provides it today.
    onLoadingChange,
}) => {

    const [accessDetails, setAccessDetails] = useState(null);
    const [userAccounts, setUserAccounts] = useState(null);
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [userProperties, setUserProperties] = useState(null);

    const [showAccountSelector, setShowAccountSelector] = useState(false);
    const [showPropertySelector, setShowPropertySelector] = useState(false);

    const missingScopes = requiredScopes.filter((scope) => !grantedScopes.includes(scope));
    const allScopesGranted = missingScopes.length === 0;
    const isCompleted = allScopesGranted && providerProjectId != null;

    const setGoogleAnalyticsProperty = useCallback((property) => {
        // Function to handle selected property
        // console.log('Selected Google Analytics Property:', property);
        onLoadingChange?.(true);
        getCMResponse('/datasource/update-datasource-details', 'POST', {
            access_id: accessDetails.id,
            property_id: property.id,
        }).then((response) => {
            // console.log('Google Analytics property set successfully:', response);
            setShowPropertySelector(false);
            onGrantedScopesUpdate();
        }).catch((error) => {
            console.error('Error setting Google Analytics property:', error);
        }).finally(() => {
            onLoadingChange?.(false);
        });
        // Further processing can be done here
    }, [accessDetails, onGrantedScopesUpdate, onLoadingChange]);

    const getUserAnalyticsAccounts = useCallback(() => {
        return new Promise((resolve, reject) => {
            getCMResponse('/datasource/google-analytics/list-user-accounts', 'GET', { project_id: project.id })
                .then((response) => {
                    resolve(response.accounts);
                })
                .catch((error) => {
                    console.error('Error fetching Google Analytics accounts:', error);
                    reject(error);
                });
        });
    }, [project.id]);

    // Bug fix: grant-access-ga-live-call-on-load/trd.md, Section 1, R1 --
    // this mount-time effect used to key off `allScopesGranted`, which is
    // derived purely from the DB row's granted scopes, not token/connection
    // validity. That meant this effect fired a live call to Google's GA
    // Admin API on every /grant-access load for any already-connected
    // project, independent of any user interaction with this tile, and
    // regardless of whether the component was about to render its inert
    // `isCompleted` branch below (which never consulted this effect at
    // all). Per the TRD's AC1 (no call when `isConnected` -- parent-derived
    // from `data_sources.status === 'ACTIVE'`, the canonical connected
    // signal per Scoping Decision (a) -- is true) and its AC3-equivalent
    // (no call either when `isConnected` is false but scopes alone are
    // granted, proving this is no longer scope-driven), this effect no
    // longer auto-fetches on mount under any condition -- bringing
    // `GoogleAnalyticsAuth` in line with the other four auth components
    // (GoogleAdsAuth/GoogleSearchConsoleAuth/GoogleMerchantCenterAuth/
    // MetaAuth), none of which have ever had a mount-time effect that
    // fires a live accounts call (report.md Verification note 3) -- only
    // a user-triggered post-OAuth-popup callback does that, for all five
    // components alike. `allScopesGranted` is unchanged for its other
    // existing use (the `isCompleted` branch below). `handleAuthCallback`'s
    // own unconditional call to getUserAnalyticsAccounts() (the
    // user-triggered post-OAuth-popup flow) is untouched by this change --
    // see TRD Constraint-risk callout. `isConnected` is still accepted as a
    // prop (R1) so this component's signature matches what
    // GrantOnboardingAccess now threads into every auth component (R2).
    useEffect(() => {
        // Intentionally a no-op with respect to fetching -- see comment
        // above. Kept as an explicit effect (rather than deleted outright)
        // so `isConnected` stays a live, exercised dependency here rather
        // than an accepted-but-ignored prop, in case future logic needs to
        // key off a mount-time connection-state transition.
    }, [isConnected]);

    useEffect(() => {
        if (selectedAccount) {
            onLoadingChange?.(true);
            getCMResponse('/datasource/google-analytics/list-user-properties', 'GET', {
                project_id: project.id,
                account_id: selectedAccount.id
            }).then((response) => {
                setUserProperties(response.properties);
                setShowPropertySelector(true);
                setShowAccountSelector(false);
            }).catch((error) => {
                console.error('Error fetching Google Analytics properties:', error);
            }).finally(() => {
                onLoadingChange?.(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAccount]);

    const handleAuthCallback = useCallback((accessDetailsResponse) => {
        // Callback function passed to GoogleAuth. onLoadingChange(true) was
        // already raised by GoogleDataAccess the moment the OAuth popup
        // resolved -- this call is the second leg of that same in-flight
        // span (account list fetch), so it's the one that clears it, on
        // either outcome.
        setAccessDetails(accessDetailsResponse);
        getUserAnalyticsAccounts().then((accounts) => {
            setUserAccounts(accounts);
            setShowAccountSelector(true);
        }).finally(() => {
            onLoadingChange?.(false);
        });
    }, [getUserAnalyticsAccounts, onLoadingChange]);

    // If completed, show the icon but make it unclickable. Bug fix:
    // datasource-reconnect-menu-noop/trd.md, Requirement 2 (Gap B) --
    // GoogleDataAccess must stay mounted even once connected (it already
    // renders null of its own accord in managed mode, so this causes no
    // visual change) so its unconditional onLoginReady effect fires and
    // loginRefs.current['GOOGLEANALYTICS'] gets populated, matching
    // MetaAuth/GoogleMerchantCenterAuth's pattern of never gating their
    // login-registration effect behind a connected-state early return.
    if (isCompleted) {
        return (
            <div className={styles.container}>
                <img
                    src={GoogleAnalyticsIcon}
                    alt="Google Analytics - Connected"
                    className={`${styles.iconButton} ${styles.completed}`}
                />
                <GoogleDataAccess
                    requiredScopes={requiredScopes}
                    onAccessGrant={handleAuthCallback}
                    icon={GoogleAnalyticsIcon}
                    project={project}
                    provider="GOOGLEANALYTICS"
                    onLoginReady={onLoginReady}
                    onLoadingChange={onLoadingChange}
                />
            </div>
        );
    }

    // If not all scopes are granted, show GoogleAuth component
    return (
        <div className={styles.container}>
            {
                !showAccountSelector && !showPropertySelector ? (
                    <GoogleDataAccess
                        requiredScopes={requiredScopes}
                        onAccessGrant={handleAuthCallback}
                        icon={GoogleAnalyticsIcon}
                        project={project}
                        provider="GOOGLEANALYTICS"
                        onLoginReady={onLoginReady}
                        onLoadingChange={onLoadingChange}
                    /> ): (
                        showAccountSelector ? 
                            <AccountSelector
                                userAccounts={userAccounts}
                                setSelectedAccount={setSelectedAccount}
                                setShowAccountSelector={setShowAccountSelector}
                            />
                         : <PropertySelector 
                            userProperties={userProperties}
                            setSelectedProperty={() => {}}
                            setShowPropertySelector={setShowPropertySelector}
                            setGoogleAnalyticsProperty={setGoogleAnalyticsProperty}
                        />
                    )
                }
        </div>
    );
    
}


export default GoogleAnalyticsAuth;